import React from 'react';
import { AlertTriangle, CheckCircle, Brain, Target, TrendingUp, MessageSquare, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

const severityBadgeClass = (severity) => {
  if (severity === 'CRITICO') return 'bg-red-100 text-red-700 border-red-300';
  if (severity === 'ALTO') return 'bg-orange-100 text-orange-700 border-orange-300';
  if (severity === 'MEDIO') return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-green-100 text-green-700 border-green-300';
};

export const AIAnalysisCard = ({ aiAnalysis }) => {
  if (!aiAnalysis || !aiAnalysis.ai_enabled) return null;

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white" data-testid="ai-analysis-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-purple-900">
          <Brain className="w-5 h-5 text-purple-600" />
          Análise Inteligente
        </CardTitle>
        <CardDescription>
          Análise automática por IA • {aiAnalysis.analyzed_at ? new Date(aiAnalysis.analyzed_at).toLocaleString('pt-PT') : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {aiAnalysis.error ? (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-700">{aiAnalysis.error}</AlertDescription>
          </Alert>
        ) : (
          <>
            {aiAnalysis.summary && (
              <div className="bg-white rounded-lg p-4 border border-purple-100">
                <p className="text-sm text-slate-700">{aiAnalysis.summary}</p>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {aiAnalysis.suggested_category && (
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                    <Target className="w-3 h-3" />
                    Categoria Sugerida
                  </div>
                  <p className="text-sm font-medium text-slate-900">{aiAnalysis.suggested_category}</p>
                </div>
              )}

              {aiAnalysis.severity && (
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                    <AlertTriangle className="w-3 h-3" />
                    Gravidade
                  </div>
                  <Badge variant="outline" className={severityBadgeClass(aiAnalysis.severity)}>
                    {aiAnalysis.severity}
                  </Badge>
                </div>
              )}

              {aiAnalysis.urgency_score && (
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                    <TrendingUp className="w-3 h-3" />
                    Urgência
                  </div>
                  <p className="text-sm font-medium text-slate-900">{aiAnalysis.urgency_score}/10</p>
                </div>
              )}

              {aiAnalysis.sentiment && (
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                    <MessageSquare className="w-3 h-3" />
                    Sentimento
                  </div>
                  <p className="text-sm font-medium text-slate-900">{aiAnalysis.sentiment}</p>
                </div>
              )}
            </div>

            {aiAnalysis.risk_indicators && aiAnalysis.risk_indicators.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-2">Indicadores de Risco</p>
                <div className="flex flex-wrap gap-2">
                  {aiAnalysis.risk_indicators.map((risk) => (
                    <Badge key={`risk-${risk}`} variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      {risk}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {aiAnalysis.recommended_actions && aiAnalysis.recommended_actions.length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-2">Ações Recomendadas</p>
                <ul className="space-y-1">
                  {aiAnalysis.recommended_actions.map((action) => (
                    <li key={`ra-${action}`} className="text-sm text-slate-700 flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {aiAnalysis.key_entities && (
              <div className="grid grid-cols-3 gap-3 text-sm">
                {aiAnalysis.key_entities.people?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Pessoas</p>
                    <p className="text-slate-700">{aiAnalysis.key_entities.people.join(', ')}</p>
                  </div>
                )}
                {aiAnalysis.key_entities.departments?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Departamentos</p>
                    <p className="text-slate-700">{aiAnalysis.key_entities.departments.join(', ')}</p>
                  </div>
                )}
                {aiAnalysis.key_entities.dates?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Datas</p>
                    <p className="text-slate-700">{aiAnalysis.key_entities.dates.join(', ')}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
